import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Settings, 
  MessageSquare, 
  Clock, 
  Users, 
  Phone,
  Globe,
  Save,
  RefreshCw,
  AlertCircle,
  Check,
  ChevronLeft,
  Trash2,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const TIMEZONES = [
  { value: 'Asia/Dubai', label: 'Dubai (GMT+4)' },
  { value: 'Asia/Kolkata', label: 'India (GMT+5:30)' },
  { value: 'Europe/London', label: 'London (GMT+0)' },
  { value: 'America/New_York', label: 'New York (GMT-5)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8)' },
  { value: 'Asia/Singapore', label: 'Singapore (GMT+8)' },
  { value: 'Australia/Sydney', label: 'Sydney (GMT+11)' }
];

const DEFAULT_WORKING_HOURS = [
  { day: 'monday', enabled: true, start: '09:00', end: '18:00' },
  { day: 'tuesday', enabled: true, start: '09:00', end: '18:00' },
  { day: 'wednesday', enabled: true, start: '09:00', end: '18:00' },
  { day: 'thursday', enabled: true, start: '09:00', end: '18:00' },
  { day: 'friday', enabled: true, start: '09:00', end: '18:00' },
  { day: 'saturday', enabled: false, start: '09:00', end: '18:00' },
  { day: 'sunday', enabled: false, start: '09:00', end: '18:00' }
];

export default function ChatFlowConfig() {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [website, setWebsite] = useState(null);
  const [config, setConfig] = useState({
    // Messages
    welcome_message: 'Hi! How can we help you today?',
    agent_connected_message: 'You are now connected with our sales agent.',
    no_agent_message: 'Thank you for reaching out. Our sales agent will connect with you shortly.',
    offline_message: "We're currently offline. Please leave a message and we'll get back to you.",
    
    // Mobile field settings
    enable_mobile_field: true,
    mobile_field_required: false,
    default_country_code: '+971',
    
    // Agent availability settings
    enable_agent_routing: true,
    agent_timeout_seconds: 30,
    
    // Working hours
    enable_working_hours: false,
    working_hours_timezone: 'Asia/Dubai',
    working_hours: DEFAULT_WORKING_HOURS,
    
    // Lead creation settings
    enable_auto_lead_creation: true,
    auto_lead_status: 'new',
    auto_lead_source: 'Chat Widget',
    
    // Fallback responses
    fallback_responses: []
  });
  
  const [countryCodes, setCountryCodes] = useState([]);

  useEffect(() => {
    fetchWebsite();
    fetchConfig();
    fetchCountryCodes();
  }, [websiteId]);

  const fetchWebsite = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/live-chat/websites/${websiteId}?token=${token}`);
      const data = await res.json();
      if (data.success) {
        setWebsite(data.website);
      }
    } catch (err) {
      console.error('Error fetching website:', err);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/live-chat/websites/${websiteId}/chat-flow-config?token=${token}`);
      const data = await res.json();
      if (data.success && data.config) {
        setConfig(prev => ({
          ...prev,
          ...data.config,
          working_hours: data.config.working_hours || DEFAULT_WORKING_HOURS
        }));
      }
    } catch (err) {
      console.error('Error fetching config:', err);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountryCodes = async () => {
    try {
      const res = await fetch(`${API}/api/live-chat/country-codes`);
      const data = await res.json();
      if (data.success) {
        setCountryCodes(data.countries);
      }
    } catch (err) {
      console.error('Error fetching country codes:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = sessionStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/live-chat/websites/${websiteId}/chat-flow-config?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Configuration saved successfully');
      } else {
        throw new Error(data.detail || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving config:', err);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to default settings?')) return;
    
    try {
      setSaving(true);
      const token = sessionStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/live-chat/websites/${websiteId}/chat-flow-config/reset?token=${token}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Configuration reset to defaults');
        fetchConfig();
      }
    } catch (err) {
      console.error('Error resetting config:', err);
      toast.error('Failed to reset configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateWorkingHours = (dayKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      working_hours: prev.working_hours.map(day => 
        day.day === dayKey ? { ...day, [field]: value } : day
      )
    }));
  };

  const addFallbackResponse = () => {
    setConfig(prev => ({
      ...prev,
      fallback_responses: [
        ...prev.fallback_responses,
        { trigger: '', response: '' }
      ]
    }));
  };

  const updateFallbackResponse = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      fallback_responses: prev.fallback_responses.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeFallbackResponse = (index) => {
    setConfig(prev => ({
      ...prev,
      fallback_responses: prev.fallback_responses.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="chat-flow-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="chat-flow-config-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/live-chat/websites')}
            data-testid="back-to-websites-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Chat Flow Configuration</h1>
            {website && (
              <p className="text-slate-500 text-sm mt-1">
                <Globe className="w-4 h-4 inline mr-1" />
                {website.name} ({website.domain})
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleReset}
            disabled={saving}
            data-testid="reset-config-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-config-btn"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="messages" className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="messages" className="data-[state=active]:bg-white">
            <MessageSquare className="w-4 h-4 mr-2" />
            Messages
          </TabsTrigger>
          <TabsTrigger value="mobile" className="data-[state=active]:bg-white">
            <Phone className="w-4 h-4 mr-2" />
            Mobile Field
          </TabsTrigger>
          <TabsTrigger value="routing" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" />
            Agent Routing
          </TabsTrigger>
          <TabsTrigger value="hours" className="data-[state=active]:bg-white">
            <Clock className="w-4 h-4 mr-2" />
            Working Hours
          </TabsTrigger>
          <TabsTrigger value="leads" className="data-[state=active]:bg-white">
            <Settings className="w-4 h-4 mr-2" />
            Lead Creation
          </TabsTrigger>
        </TabsList>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Chat Messages</h3>
            <p className="text-slate-500 text-sm mb-6">Customize the messages shown to visitors during different chat states.</p>
            
            <div className="space-y-6">
              <div>
                <Label>Welcome Message</Label>
                <p className="text-xs text-slate-500 mb-2">Shown when visitor opens the chat widget</p>
                <Textarea
                  value={config.welcome_message}
                  onChange={(e) => setConfig({ ...config, welcome_message: e.target.value })}
                  placeholder="Hi! How can we help you today?"
                  rows={2}
                  data-testid="welcome-message-input"
                />
              </div>

              <div>
                <Label>Agent Connected Message</Label>
                <p className="text-xs text-slate-500 mb-2">Shown when visitor is connected to a live agent</p>
                <Textarea
                  value={config.agent_connected_message}
                  onChange={(e) => setConfig({ ...config, agent_connected_message: e.target.value })}
                  placeholder="You are now connected with our sales agent."
                  rows={2}
                  data-testid="agent-connected-message-input"
                />
              </div>

              <div>
                <Label>No Agent Available Message</Label>
                <p className="text-xs text-slate-500 mb-2">Shown when no agents are online (lead will be created)</p>
                <Textarea
                  value={config.no_agent_message}
                  onChange={(e) => setConfig({ ...config, no_agent_message: e.target.value })}
                  placeholder="Thank you for reaching out. Our sales agent will connect with you shortly."
                  rows={2}
                  data-testid="no-agent-message-input"
                />
              </div>

              <div>
                <Label>Offline Message</Label>
                <p className="text-xs text-slate-500 mb-2">Shown outside working hours (if enabled)</p>
                <Textarea
                  value={config.offline_message}
                  onChange={(e) => setConfig({ ...config, offline_message: e.target.value })}
                  placeholder="We're currently offline. Please leave a message and we'll get back to you."
                  rows={2}
                  data-testid="offline-message-input"
                />
              </div>

              {/* Fallback Responses */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label>Fallback Responses</Label>
                    <p className="text-xs text-slate-500">Auto-responses based on keywords</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={addFallbackResponse}
                    data-testid="add-fallback-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Response
                  </Button>
                </div>
                
                {config.fallback_responses.map((item, index) => (
                  <div key={index} className="flex gap-3 items-start mb-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <Input
                        placeholder="Trigger keyword (e.g., pricing)"
                        value={item.trigger}
                        onChange={(e) => updateFallbackResponse(index, 'trigger', e.target.value)}
                        className="mb-2"
                        data-testid={`fallback-trigger-${index}`}
                      />
                      <Textarea
                        placeholder="Response message"
                        value={item.response}
                        onChange={(e) => updateFallbackResponse(index, 'response', e.target.value)}
                        rows={2}
                        data-testid={`fallback-response-${index}`}
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeFallbackResponse(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-fallback-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Mobile Field Tab */}
        <TabsContent value="mobile">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Mobile Number Field</h3>
            <p className="text-slate-500 text-sm mb-6">Configure the mobile number field with country code selector.</p>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base">Enable Mobile Field</Label>
                  <p className="text-xs text-slate-500 mt-1">Show mobile number input in the pre-chat form</p>
                </div>
                <Switch
                  checked={config.enable_mobile_field}
                  onCheckedChange={(checked) => setConfig({ ...config, enable_mobile_field: checked })}
                  data-testid="enable-mobile-field-switch"
                />
              </div>

              {config.enable_mobile_field && (
                <>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-base">Required Field</Label>
                      <p className="text-xs text-slate-500 mt-1">Make mobile number mandatory to start chat</p>
                    </div>
                    <Switch
                      checked={config.mobile_field_required}
                      onCheckedChange={(checked) => setConfig({ ...config, mobile_field_required: checked })}
                      data-testid="mobile-required-switch"
                    />
                  </div>

                  <div>
                    <Label>Default Country Code</Label>
                    <p className="text-xs text-slate-500 mb-2">Pre-selected country code for visitors</p>
                    <Select 
                      value={config.default_country_code}
                      onValueChange={(v) => setConfig({ ...config, default_country_code: v })}
                    >
                      <SelectTrigger className="w-64" data-testid="default-country-code-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        {countryCodes.map(country => (
                          <SelectItem key={country.code} value={country.dial_code}>
                            {country.dial_code} - {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Agent Routing Tab */}
        <TabsContent value="routing">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Agent Routing</h3>
            <p className="text-slate-500 text-sm mb-6">Configure how chats are routed based on agent availability.</p>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base">Enable Smart Routing</Label>
                  <p className="text-xs text-slate-500 mt-1">Automatically check agent availability before connecting</p>
                </div>
                <Switch
                  checked={config.enable_agent_routing}
                  onCheckedChange={(checked) => setConfig({ ...config, enable_agent_routing: checked })}
                  data-testid="enable-routing-switch"
                />
              </div>

              {config.enable_agent_routing && (
                <div>
                  <Label>Agent Response Timeout</Label>
                  <p className="text-xs text-slate-500 mb-2">How long to wait for an agent before showing offline message (seconds)</p>
                  <Input
                    type="number"
                    value={config.agent_timeout_seconds}
                    onChange={(e) => setConfig({ ...config, agent_timeout_seconds: parseInt(e.target.value) || 30 })}
                    min={10}
                    max={120}
                    className="w-32"
                    data-testid="agent-timeout-input"
                  />
                </div>
              )}

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <strong>How it works:</strong>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      <li>When a visitor starts a chat, the system checks if any agent is online</li>
                      <li>If an agent is available, the chat is connected immediately</li>
                      <li>If no agent is available, a lead is automatically created in your CRM</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Working Hours Tab */}
        <TabsContent value="hours">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Working Hours</h3>
            <p className="text-slate-500 text-sm mb-6">Define when your team is available for live chat.</p>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base">Enable Working Hours</Label>
                  <p className="text-xs text-slate-500 mt-1">Show offline message outside defined hours</p>
                </div>
                <Switch
                  checked={config.enable_working_hours}
                  onCheckedChange={(checked) => setConfig({ ...config, enable_working_hours: checked })}
                  data-testid="enable-working-hours-switch"
                />
              </div>

              {config.enable_working_hours && (
                <>
                  <div>
                    <Label>Timezone</Label>
                    <Select 
                      value={config.working_hours_timezone}
                      onValueChange={(v) => setConfig({ ...config, working_hours_timezone: v })}
                    >
                      <SelectTrigger className="w-64" data-testid="timezone-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map(tz => (
                          <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 font-medium text-slate-600">Day</th>
                          <th className="text-left p-3 font-medium text-slate-600">Active</th>
                          <th className="text-left p-3 font-medium text-slate-600">Start Time</th>
                          <th className="text-left p-3 font-medium text-slate-600">End Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {DAYS_OF_WEEK.map(day => {
                          const dayConfig = config.working_hours.find(d => d.day === day.key) || {
                            day: day.key,
                            enabled: false,
                            start: '09:00',
                            end: '18:00'
                          };
                          return (
                            <tr key={day.key} className="border-t">
                              <td className="p-3 font-medium">{day.label}</td>
                              <td className="p-3">
                                <Switch
                                  checked={dayConfig.enabled}
                                  onCheckedChange={(checked) => updateWorkingHours(day.key, 'enabled', checked)}
                                  data-testid={`${day.key}-enabled-switch`}
                                />
                              </td>
                              <td className="p-3">
                                <Input
                                  type="time"
                                  value={dayConfig.start}
                                  onChange={(e) => updateWorkingHours(day.key, 'start', e.target.value)}
                                  disabled={!dayConfig.enabled}
                                  className="w-32"
                                  data-testid={`${day.key}-start-time`}
                                />
                              </td>
                              <td className="p-3">
                                <Input
                                  type="time"
                                  value={dayConfig.end}
                                  onChange={(e) => updateWorkingHours(day.key, 'end', e.target.value)}
                                  disabled={!dayConfig.enabled}
                                  className="w-32"
                                  data-testid={`${day.key}-end-time`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Lead Creation Tab */}
        <TabsContent value="leads">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Automatic Lead Creation</h3>
            <p className="text-slate-500 text-sm mb-6">Configure how leads are created when no agent is available.</p>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base">Enable Auto Lead Creation</Label>
                  <p className="text-xs text-slate-500 mt-1">Automatically create a lead when no agent is online</p>
                </div>
                <Switch
                  checked={config.enable_auto_lead_creation}
                  onCheckedChange={(checked) => setConfig({ ...config, enable_auto_lead_creation: checked })}
                  data-testid="enable-auto-lead-switch"
                />
              </div>

              {config.enable_auto_lead_creation && (
                <>
                  <div>
                    <Label>Default Lead Status</Label>
                    <p className="text-xs text-slate-500 mb-2">Status assigned to auto-created leads</p>
                    <Select 
                      value={config.auto_lead_status}
                      onValueChange={(v) => setConfig({ ...config, auto_lead_status: v })}
                    >
                      <SelectTrigger className="w-48" data-testid="auto-lead-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="interested">Interested</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Lead Source Label</Label>
                    <p className="text-xs text-slate-500 mb-2">Source value for tracking</p>
                    <Input
                      value={config.auto_lead_source}
                      onChange={(e) => setConfig({ ...config, auto_lead_source: e.target.value })}
                      placeholder="Chat Widget"
                      className="w-64"
                      data-testid="auto-lead-source-input"
                    />
                  </div>
                </>
              )}

              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex gap-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <div className="text-sm text-emerald-800">
                    <strong>What gets captured:</strong>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      <li>Visitor name, email, and phone number</li>
                      <li>Initial chat message</li>
                      <li>Page URL and referrer</li>
                      <li>Timestamp and website source</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
